import { Section, Question, CheckboxGroup, SelectField, WorkshopNote } from "./PortalShared";
import { CheckCircle } from "lucide-react";

const DELIVERABLES = ["Wireframe", "Copywriting", "Design (Figma)", "Build", "Tracking-Setup", "QA / Testing", "Finale Freigabe"];
const STATUS_OPTIONS = ["Offen", "In Arbeit", "Erledigt"];

export default function PortalPhase3({ formData, updateField, toggleCheckbox }) {
  const updateDeliverable = (index, field, value) => {
    const deliverables = [...(formData.t_deliverables || DELIVERABLES.map(d => ({ name: d, owner: "", deadline: "", status: "Offen" })))];
    deliverables[index] = { ...deliverables[index], [field]: value };
    updateField("t_deliverables", deliverables);
  };

  const deliverables = formData.t_deliverables || DELIVERABLES.map(d => ({ name: d, owner: "", deadline: "", status: "Offen" }));

  const goLiveSteps = [
    "Zugänge & Logins bereitstellen (CMS, Domain, CRM)",
    "Asset-Ordner anlegen und befüllen",
    "Tracking & CRM-Anbindung konfigurieren",
    "Consent-Management einrichten",
    "Review-Termine in Kalender eintragen",
    "Legal Pages freigeben (Impressum, DSE)",
    "Go-live Datum mit allen Stakeholdern bestätigen",
    "Post-launch Monitoring einrichten (GA4, Alerts)",
  ];

  return (
    <>
      {/* === 09 Design & Assets === */}
      <Section num="09" title="Design & Assets">
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <Question label="CI/CD vorhanden?"
            info="Corporate Identity / Corporate Design — Ihre bestehenden Markenrichtlinien (Logo, Farben, Schriften, Tonalität).">
            <input type="url" className="portal-input mb-1.5" value={formData.t_brand_link || ""}
              onChange={e => updateField("t_brand_link", e.target.value)}
              placeholder="Link zu Brand-Guidelines / Figma …" data-testid="field-t_brand_link" />
            <input type="text" className="portal-input" value={formData.t_brand_notes || ""}
              onChange={e => updateField("t_brand_notes", e.target.value)}
              placeholder="Ablage / Hinweise …" data-testid="field-t_brand_notes" />
          </Question>
          <Question label="Bildstil">
            <CheckboxGroup fieldKey="t_image_style"
              options={["People", "Abstract", "Produkt", "Illustration", "Screenshots"]}
              formData={formData} toggleCheckbox={toggleCheckbox} updateField={updateField} />
          </Question>
        </div>

        <Question label="Vorhandene Assets"
          info="Welche visuellen Elemente stehen bereits zur Verfügung? Das bestimmt, was neu erstellt werden muss.">
          <CheckboxGroup fieldKey="t_existing_assets"
            options={["Logo (SVG)", "Bilder (eigene)", "Icons", "Video", "UI Screenshots", "Illustrationen"]}
            formData={formData} toggleCheckbox={toggleCheckbox} updateField={updateField} />
          <input type="url" className="portal-input mt-2" value={formData.t_asset_link || ""}
            onChange={e => updateField("t_asset_link", e.target.value)}
            placeholder="Asset-Ablage Link …" data-testid="field-t_asset_link" />
        </Question>

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <Question label="Seiten die gefallen">
            <textarea className="portal-input portal-textarea" value={formData.t_sites_like || ""}
              onChange={e => updateField("t_sites_like", e.target.value)}
              placeholder="URL — weil …" data-testid="field-t_sites_like" />
          </Question>
          <Question label="Seiten die nicht gefallen">
            <textarea className="portal-input portal-textarea" value={formData.t_sites_dislike || ""}
              onChange={e => updateField("t_sites_dislike", e.target.value)}
              placeholder="URL — weil …" data-testid="field-t_sites_dislike" />
          </Question>
        </div>

        <Question label="Copy-Tonalität"
          info="Der Ton der Texte auf Ihrer Seite — von sachlich-zurückhaltend bis direkt und handlungsorientiert.">
          <SelectField fieldKey="t_copy_tonality"
            options={["Consulting premium — ruhig, präzise, substanziell", "Performance — direkt, nutzengetrieben, handlungsauslösend", "Mix aus beidem"]}
            formData={formData} updateField={updateField} />
        </Question>
      </Section>

      {/* === 10 Technik & Integrationen === */}
      <Section num="10" title="Technik & Integrationen">
        <WorkshopNote text="Intern: Diese Angaben werden von LAB336 mit dem technischen Kontakt des Kunden besprochen." />

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <Question label="Domain / Subdomain">
            <input type="url" className="portal-input mb-1.5" value={formData.t_domain || ""}
              onChange={e => updateField("t_domain", e.target.value)}
              placeholder="https://www.example.ch" data-testid="field-t_domain" />
            <input type="text" className="portal-input" value={formData.t_dns || ""}
              onChange={e => updateField("t_dns", e.target.value)}
              placeholder="DNS-Verantwortung: …" data-testid="field-t_dns" />
          </Question>
          <Question label="Hosting / Tech-Stack">
            <CheckboxGroup fieldKey="t_tech_stack"
              options={["Webflow", "WordPress", "Next.js", "Framer", "Anderes"]}
              otherKey="t_tech_details"
              formData={formData} toggleCheckbox={toggleCheckbox} updateField={updateField} />
          </Question>
        </div>

        <Question label="Form-Integrationen"
          info="CRM- und Marketing-Tools, an die das Kontaktformular angebunden werden soll — z.B. für automatische Lead-Erfassung.">
          <CheckboxGroup fieldKey="t_form_integrations"
            options={["HubSpot", "Salesforce", "Pipedrive", "Mailchimp / Klaviyo", "Slack", "Zapier / Make", "Custom API"]}
            formData={formData} toggleCheckbox={toggleCheckbox} updateField={updateField} />
          <input type="text" className="portal-input mt-2" value={formData.t_integration_details || ""}
            onChange={e => updateField("t_integration_details", e.target.value)}
            placeholder="Details / Zugänge klären mit: …" data-testid="field-t_integration_details" />
        </Question>

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <Question label="Tracking"
            info="Welche Analyse-Tools werden eingesetzt? GA4 = Google Analytics 4, GTM = Google Tag Manager.">
            <CheckboxGroup fieldKey="t_tracking"
              options={["GA4", "GTM", "LinkedIn", "Meta Pixel", "Matomo", "Plausible"]}
              formData={formData} toggleCheckbox={toggleCheckbox} updateField={updateField} />
          </Question>
          <Question label="Performance-Ziele" text="Core Web Vitals / Lighthouse"
            info="Lighthouse misst die technische Qualität Ihrer Website (Geschwindigkeit, Zugänglichkeit). LCP = Largest Contentful Paint (wie schnell die Seite sichtbar wird).">
            <input type="text" className="portal-input" value={formData.t_performance_goals || ""}
              onChange={e => updateField("t_performance_goals", e.target.value)}
              placeholder="z.B. Lighthouse ≥ 90, LCP ≤ 2.5 s …" data-testid="field-t_performance_goals" />
          </Question>
        </div>

        <Question label="SEO-Basics" text="Verantwortung für Title/Meta, OpenGraph, Sitemap, Schema"
          info="SEO = Suchmaschinenoptimierung. Wer pflegt die technischen SEO-Daten (Seitentitel, Beschreibungen, strukturierte Daten)?">
          <input type="text" className="portal-input" value={formData.t_seo_basics || ""}
            onChange={e => updateField("t_seo_basics", e.target.value)}
            placeholder="Wer liefert / pflegt die SEO-Daten? …" data-testid="field-t_seo_basics" />
        </Question>
      </Section>

      {/* === 11 Tracking, Consent & Recht === */}
      <Section num="11" title="Tracking, Consent & Recht">
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <Question label="Consent Management (CMP)"
            info="CMP = Consent Management Platform. Ein Tool, das die Cookie-Einwilligung der Website-Besucher verwaltet (gesetzlich vorgeschrieben).">
            <SelectField fieldKey="t_consent_management"
              options={["Cookiebot", "Usercentrics", "OneTrust", "Borlabs Cookie", "Noch offen"]}
              formData={formData} updateField={updateField} />
          </Question>
          <Question label="Pflicht-Events"
            info="Events, die im Tracking erfasst werden müssen — z.B. um den ROI von Werbekampagnen messen zu können.">
            <CheckboxGroup fieldKey="t_required_events"
              options={["CTA Click", "Form Start", "Form Submit", "Booking Confirmed", "Scroll Depth"]}
              formData={formData} toggleCheckbox={toggleCheckbox} updateField={updateField} />
          </Question>
        </div>

        <Question label="Legal Pages">
          <CheckboxGroup fieldKey="t_legal_pages"
            options={["Impressum vorhanden", "Datenschutzerklärung vorhanden", "AGB vorhanden", "Muss erstellt werden"]}
            formData={formData} toggleCheckbox={toggleCheckbox} updateField={updateField} />
          <input type="text" className="portal-input mt-2" value={formData.t_legal_responsibility || ""}
            onChange={e => updateField("t_legal_responsibility", e.target.value)}
            placeholder="Zuständigkeit + Freigabedauer: …" data-testid="field-t_legal_responsibility" />
        </Question>
      </Section>

      {/* === 12 Scope, Timeline & Freigaben === */}
      <Section num="12" title="Scope, Timeline & Freigaben">
        <Question label="Deliverables im Scope">
          <CheckboxGroup fieldKey="t_deliverables_scope"
            options={["Wireframe", "Copywriting", "Design (Figma)", "Build / Entwicklung", "Tracking-Setup", "QA / Testing", "Go-live Support"]}
            formData={formData} toggleCheckbox={toggleCheckbox} updateField={updateField} />
        </Question>

        {/* Deliverables Table */}
        <div className="overflow-x-auto mt-4">
          <table className="w-full border border-[#CBD5E1] text-sm">
            <thead>
              <tr className="bg-[#0F172A] text-white">
                <th className="text-left px-3 py-2 font-semibold text-[0.75rem] uppercase" style={{ letterSpacing: "0.1em" }}>Deliverable</th>
                <th className="text-left px-3 py-2 font-semibold text-[0.75rem] uppercase" style={{ letterSpacing: "0.1em" }}>Zuständig</th>
                <th className="text-left px-3 py-2 font-semibold text-[0.75rem] uppercase" style={{ letterSpacing: "0.1em" }}>Deadline</th>
                <th className="text-left px-3 py-2 font-semibold text-[0.75rem] uppercase" style={{ letterSpacing: "0.1em" }}>Status</th>
              </tr>
            </thead>
            <tbody>
              {deliverables.map((d, i) => (
                <tr key={i} className="border-t border-[#CBD5E1]">
                  <td className="px-3 py-2 font-medium text-[#0F172A] bg-[#F8F9FA] whitespace-nowrap">{d.name}</td>
                  <td className="px-2 py-1.5">
                    <input type="text" className="portal-input !py-1.5 !text-sm" value={d.owner}
                      onChange={e => updateDeliverable(i, "owner", e.target.value)} placeholder="z.B. LAB336" />
                  </td>
                  <td className="px-2 py-1.5">
                    <input type="date" className="portal-input !py-1.5 !text-sm" value={d.deadline}
                      onChange={e => updateDeliverable(i, "deadline", e.target.value)} />
                  </td>
                  <td className="px-2 py-1.5">
                    <select className="portal-input !py-1.5 !text-sm" value={d.status}
                      onChange={e => updateDeliverable(i, "status", e.target.value)}>
                      {STATUS_OPTIONS.map(s => <option key={s} value={s}>{s}</option>)}
                    </select>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mt-4">
          <Question label="Go-live Datum">
            <input type="date" className="portal-input" value={formData.t_golive_date || ""}
              onChange={e => updateField("t_golive_date", e.target.value)} data-testid="field-t_golive_date" />
          </Question>
          <Question label="Buffer eingeplant?">
            <SelectField fieldKey="t_buffer"
              options={["Ja — 1 Woche", "Ja — 2 Wochen", "Nein — hartes Datum"]}
              formData={formData} updateField={updateField} />
          </Question>
        </div>
      </Section>

      {/* === Go-live Checkliste === */}
      <div className="bg-[#0F172A] border-none mb-7" data-testid="section-checklist">
        <div className="bg-[#D4A373] flex items-center gap-3.5 px-6 py-3.5">
          <span className="bg-[#0F172A] text-[#D4A373] text-[0.7rem] font-bold px-2 py-0.5"
            style={{ letterSpacing: "0.1em" }}>★</span>
          <span className="font-playfair text-base font-semibold text-[#0F172A]">Nächste Schritte — Go-live Checkliste</span>
        </div>
        <div className="p-6">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
            {goLiveSteps.map((step, i) => (
              <label key={i} className="flex items-start gap-2.5 text-[0.875rem] font-medium text-[#CBD5E1] cursor-pointer py-1.5 px-3 border border-[#334155] hover:border-[#D4A373] transition-colors">
                <input type="checkbox"
                  checked={(formData.t_checklist || []).includes(step)}
                  onChange={() => toggleCheckbox("t_checklist", step)}
                  className="accent-[#D4A373] mt-0.5 shrink-0" />
                <span>{step}</span>
              </label>
            ))}
          </div>
        </div>
      </div>
    </>
  );
}
